/**
 * The structure of the Help Center: which articles exist, how they are grouped, where they point
 * to and under which older / shorter names they are still reachable. It is pure data so that
 * every layer can name an article (`<HelpTip topicId="database-views">`) without loading any
 * text. The TEXT of every article lives in `features/help-center/content/<language>/*`, typed by
 * `HelpContent`: an article added here must be written in both languages to compile.
 *
 * Article ids are part of public URLs (`/app/help?topic=<id>`) and of bookmarks: never rename one.
 * If an article must be split or renamed, keep the old id in `HELP_TOPIC_ALIASES`.
 *
 * Links only go to routes that exist for every signed-in person: the guide never names a
 * workspace, board, database or person.
 */
export const HELP_CATEGORIES = [
  'start',
  'workspaces',
  'boards',
  'databases',
  'calendars',
  'personalization',
  'data',
  'feedback',
] as const;
export type HelpCategory = (typeof HELP_CATEGORIES)[number];

export type HelpLinkKey =
  | 'home'
  | 'myBoards'
  | 'myDatabases'
  | 'workspacesCatalog'
  | 'myTasks'
  | 'calendar'
  | 'account'
  | 'interface'
  | 'shortcuts'
  | 'notifications'
  | 'invitations'
  | 'workspaceTrash'
  | 'feedback';

export const HELP_LINK_TARGETS: Record<HelpLinkKey, string> = {
  home: '/app',
  myBoards: '/app/browse/boards',
  myDatabases: '/app/browse/databases',
  workspacesCatalog: '/app/browse/workspaces',
  myTasks: '/app/my-tasks',
  calendar: '/app/calendar',
  account: '/app/account',
  interface: '/app/settings/interface',
  shortcuts: '/app/settings/shortcuts',
  notifications: '/app/notifications',
  invitations: '/app/invitations',
  workspaceTrash: '/app/workspace-trash',
  feedback: '/app/feedback',
};

export interface HelpTopicMeta {
  id: string;
  category: HelpCategory;
  /** Application pages that help to try the feature. */
  links?: readonly HelpLinkKey[];
  /** Other articles worth reading next. */
  related?: readonly string[];
}

// The order here is the order of the contents, inside each category.
export const HELP_TOPICS = [
  // --- Getting started and navigation
  {
    id: 'getting-started',
    category: 'start',
    links: ['home'],
    related: ['concepts', 'workspaces', 'boards'],
  },
  {
    id: 'concepts',
    category: 'start',
    related: ['workspaces', 'kanban', 'databases', 'database-views'],
  },
  {
    id: 'home',
    category: 'start',
    links: ['home'],
    related: ['catalogs', 'my-tasks', 'global-calendar'],
  },
  {
    id: 'catalogs',
    category: 'start',
    links: ['myBoards', 'myDatabases', 'workspacesCatalog'],
    related: ['home', 'favorites-deck', 'searchable-selectors'],
  },
  {
    id: 'quick-create',
    category: 'start',
    links: ['home'],
    related: ['tasks', 'calendar-timeline', 'shortcuts'],
  },
  { id: 'favorites-deck', category: 'start', links: ['shortcuts'], related: ['catalogs', 'home'] },
  // --- Workspaces and access
  { id: 'workspaces', category: 'workspaces', related: ['members-teams', 'invitations', 'roles'] },
  { id: 'members-teams', category: 'workspaces', related: ['roles', 'board-access'] },
  {
    id: 'roles',
    category: 'workspaces',
    related: ['hierarchy', 'invitations', 'workspace-palettes'],
  },
  { id: 'hierarchy', category: 'workspaces', related: ['roles'] },
  {
    id: 'board-access',
    category: 'workspaces',
    related: ['roles', 'invitations', 'labels', 'database-access'],
  },
  {
    id: 'invitations',
    category: 'workspaces',
    links: ['invitations'],
    related: ['roles', 'board-access'],
  },
  {
    id: 'workspace-palettes',
    category: 'workspaces',
    related: ['color-palettes', 'roles'],
  },
  { id: 'notifications', category: 'workspaces', links: ['notifications'], related: ['comments'] },
  { id: 'realtime', category: 'workspaces', related: ['notifications', 'database-records'] },
  // --- Boards and tasks
  { id: 'boards', category: 'boards', related: ['kanban', 'board-access', 'import-export'] },
  { id: 'kanban', category: 'boards', related: ['board-filters', 'tasks', 'trash'] },
  { id: 'board-filters', category: 'boards', related: ['kanban', 'labels', 'assignments'] },
  { id: 'tasks', category: 'boards', related: ['content', 'labels', 'recurring', 'trash'] },
  { id: 'labels', category: 'boards', related: ['tasks', 'board-filters', 'board-access'] },
  { id: 'content', category: 'boards', related: ['tasks', 'comments'] },
  { id: 'comments', category: 'boards', links: ['notifications'], related: ['assignments'] },
  { id: 'assignments', category: 'boards', related: ['comments', 'roles', 'my-tasks'] },
  { id: 'history', category: 'boards', related: ['tasks', 'trash'] },
  { id: 'mysliarium', category: 'boards', related: ['tasks'] },
  // --- Universal databases
  {
    id: 'databases',
    category: 'databases',
    links: ['myDatabases'],
    related: ['database-tables', 'database-fields', 'database-views', 'concepts'],
  },
  {
    id: 'database-tables',
    category: 'databases',
    related: ['databases', 'database-fields', 'searchable-selectors'],
  },
  {
    id: 'database-fields',
    category: 'databases',
    related: ['database-records', 'database-filtering', 'database-grouping'],
  },
  {
    id: 'database-records',
    category: 'databases',
    related: ['database-fields', 'database-sections', 'database-history'],
  },
  {
    id: 'database-views',
    category: 'databases',
    related: ['database-filtering', 'database-grouping', 'database-sections'],
  },
  {
    id: 'database-filtering',
    category: 'databases',
    related: ['database-views', 'database-fields'],
  },
  {
    id: 'database-grouping',
    category: 'databases',
    related: ['database-views', 'database-sections', 'database-fields'],
  },
  {
    id: 'database-sections',
    category: 'databases',
    related: ['database-grouping', 'database-records', 'database-views'],
  },
  {
    id: 'database-portability',
    category: 'databases',
    related: ['import-export', 'database-access', 'database-history'],
  },
  {
    id: 'database-history',
    category: 'databases',
    related: ['database-records', 'trash'],
  },
  {
    id: 'database-access',
    category: 'databases',
    related: ['roles', 'board-access', 'database-portability'],
  },
  // --- Calendars and planning
  {
    id: 'my-tasks',
    category: 'calendars',
    links: ['myTasks', 'home'],
    related: ['global-calendar', 'calendar-timeline', 'assignments'],
  },
  {
    id: 'calendar-timeline',
    category: 'calendars',
    links: ['myTasks'],
    related: ['global-calendar', 'recurring', 'tasks', 'quick-create'],
  },
  {
    id: 'global-calendar',
    category: 'calendars',
    links: ['calendar', 'home'],
    related: ['calendar-sources', 'calendar-timeline', 'my-tasks'],
  },
  {
    id: 'calendar-sources',
    category: 'calendars',
    links: ['calendar', 'home'],
    related: ['global-calendar', 'searchable-selectors'],
  },
  { id: 'recurring', category: 'calendars', related: ['calendar-timeline', 'tasks'] },
  // --- Personalization and settings
  {
    id: 'interface',
    category: 'personalization',
    links: ['interface', 'account'],
    related: ['shortcuts'],
  },
  { id: 'shortcuts', category: 'personalization', links: ['shortcuts'], related: ['quick-create'] },
  { id: 'colors', category: 'personalization', related: ['color-palettes', 'boards'] },
  {
    id: 'color-palettes',
    category: 'personalization',
    related: ['colors', 'workspace-palettes'],
  },
  {
    id: 'searchable-selectors',
    category: 'personalization',
    related: ['catalogs', 'database-tables'],
  },
  // --- Data, history and recovery
  {
    id: 'trash',
    category: 'data',
    links: ['workspaceTrash'],
    related: ['history', 'database-history', 'import-export'],
  },
  {
    id: 'import-export',
    category: 'data',
    related: ['boards', 'database-portability'],
  },
  // --- Suggestions (the Feedback Center)
  {
    id: 'feedback-suggestions',
    category: 'feedback',
    links: ['feedback'],
    related: ['feedback-statuses', 'feedback-moderation', 'notifications'],
  },
  {
    id: 'feedback-statuses',
    category: 'feedback',
    links: ['feedback', 'notifications'],
    related: ['feedback-suggestions', 'feedback-moderation'],
  },
  {
    id: 'feedback-moderation',
    category: 'feedback',
    links: ['feedback'],
    related: ['feedback-statuses', 'feedback-suggestions'],
  },
] as const satisfies readonly HelpTopicMeta[];

export type HelpTopicId = (typeof HELP_TOPICS)[number]['id'];
export type HelpTopicIdOf<C extends HelpCategory> = Extract<
  (typeof HELP_TOPICS)[number],
  { category: C }
>['id'];

export const HELP_TOPIC_IDS: readonly HelpTopicId[] = HELP_TOPICS.map((topic) => topic.id);

export const DEFAULT_TOPIC: HelpTopicId = 'getting-started';

/**
 * Other names that open an article: friendly short names and the ids of articles that were
 * merged or split. An alias never shadows a real id (checked by a test).
 */
export const HELP_TOPIC_ALIASES: Readonly<Record<string, HelpTopicId>> = {
  permissions: 'roles',
  palettes: 'color-palettes',
  database: 'databases',
  calendar: 'global-calendar',
  filters: 'board-filters',
  views: 'database-views',
};

export function isHelpTopicId(value: string | null | undefined): value is HelpTopicId {
  return value != null && HELP_TOPIC_IDS.some((id) => id === value);
}

export type TopicResolution =
  { kind: 'topic'; id: HelpTopicId } | { kind: 'alias'; id: HelpTopicId } | { kind: 'missing' };

/** What a `?topic=` value points to. Nothing (no parameter) is the default article. */
export function resolveHelpTopic(value: string | null): TopicResolution {
  if (value === null || value === '') return { kind: 'topic', id: DEFAULT_TOPIC };
  if (isHelpTopicId(value)) return { kind: 'topic', id: value };
  const alias = Object.hasOwn(HELP_TOPIC_ALIASES, value) ? HELP_TOPIC_ALIASES[value] : undefined;
  return alias ? { kind: 'alias', id: alias } : { kind: 'missing' };
}

/** The one place that assembles the address of an article. */
export function helpTopicHref(id: HelpTopicId): string {
  return `/app/help?topic=${encodeURIComponent(id)}`;
}
